import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle2, XCircle, Keyboard, RefreshCw, ScanLine, X, Clock } from "lucide-react";

/* Mobile-first check-in app for exhibition staff.
   URL: /vm/checkin/{event_checkin_token}
   Access model: unlisted URL (admins rotate the token to revoke). */

export function VmCheckinPublic() {
  const { eventToken } = useParams();
  const [boot, setBoot] = useState(null);         // { event, counts }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Current preview card (before confirm) — { ticket, attendee, already_checked_in }
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);  // { attendee, ticket, duplicate }
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const lastRawRef = useRef("");

  // Boot
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/vm/checkin/${eventToken}`);
        setBoot(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Check-in link is invalid.");
      } finally { setLoading(false); }
    })();
  }, [eventToken]);

  // Cleanup camera on unmount
  useEffect(() => () => stopCamera(), []);

  async function refreshCounts() {
    try {
      const { data } = await api.get(`/public/vm/checkin/${eventToken}`);
      setBoot((prev) => (prev ? { ...prev, counts: data.counts } : data));
    } catch { /* ignore */ }
  }

  async function lookup({ ticket_number, token }) {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/vm/checkin/${eventToken}/lookup`, { ticket_number, token });
      setPreview(data);
    } catch (err) {
      setLastResult({ error: err?.response?.data?.detail || "Invalid ticket" });
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Invalid ticket");
    } finally { setBusy(false); }
  }

  async function confirm() {
    if (!preview || confirming) return;
    setConfirming(true);
    const t = preview.ticket;
    try {
      const { data } = await api.post(`/public/vm/checkin/${eventToken}/confirm`, { token: t.token });
      setLastResult({ ...data, attendee: data.attendee, ticket: data.ticket });
      setPreview(null);
      if (data.counts) setBoot((prev) => ({ ...prev, counts: data.counts }));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Check-in failed";
      setLastResult({ error: detail });
      toast.error(detail);
    } finally { setConfirming(false); }
  }

  function clearPreview() { setPreview(null); }

  // Manual entry
  function submitManual(e) {
    e.preventDefault();
    const n = Number(manual.trim());
    if (!n || Number.isNaN(n)) { toast.error("Enter a valid ticket number"); return; }
    setManualOpen(false);
    setManual("");
    lookup({ ticket_number: n });
  }

  // Camera scanning
  async function startCamera() {
    if (!("BarcodeDetector" in window)) {
      toast.error("This browser doesn't support QR scanning. Use manual entry.");
      setManualOpen(true);
      return;
    }
    try {
      detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      scanLoop();
    } catch {
      toast.error("Camera permission was blocked. Use manual entry.");
      setManualOpen(true);
    }
  }
  function stopCamera() {
    setScanning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }
  async function scanLoop() {
    if (!detectorRef.current || !videoRef.current) return;
    try {
      const codes = await detectorRef.current.detect(videoRef.current);
      if (codes && codes.length > 0) {
        const raw = codes[0].rawValue;
        if (raw && raw !== lastRawRef.current) {
          lastRawRef.current = raw;
          const m = raw.match(/(?:pass|ticket)\/([^/?#]+)/);
          const token = m ? m[1] : raw;
          await lookup({ token });
          // brief cooldown
          setTimeout(() => { lastRawRef.current = ""; }, 1500);
        }
      }
    } catch { /* transient detector errors */ }
    rafRef.current = requestAnimationFrame(scanLoop);
  }

  // Fetch fresh counts every 20s so numbers stay live even for multi-device use
  useEffect(() => {
    if (!boot) return;
    const iv = setInterval(refreshCounts, 20000);
    return () => clearInterval(iv);
  }, [boot?.event?.id]);

  if (loading) return <Shell><Center>Loading…</Center></Shell>;
  if (error || !boot) return (
    <Shell>
      <Center>
        <XCircle size={40} className="text-red-500 mx-auto mb-3" />
        <div className="font-display text-2xl font-semibold">Link invalid</div>
        <p className="text-sm text-gray-500 mt-2 max-w-xs mx-auto">{error || "This check-in link has been revoked or never existed. Ask the event organiser for a fresh link."}</p>
      </Center>
    </Shell>
  );

  const { event: ev, counts } = boot;
  const pct = counts?.seated ? Math.round((counts.checked_in / counts.seated) * 100) : 0;

  return (
    <div
      className="min-h-screen bg-gray-950 text-white flex flex-col select-none"
      style={{ minHeight: "100dvh" }}
      data-testid="vm-checkin-app"
    >
      {/* Header — brand + counters */}
      <header className="px-4 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          {ev.logo_path ? (
            <img src={fileUrl(ev.logo_path)} alt={ev.name} className="h-9 max-w-[120px] object-contain bg-white rounded-md p-1" />
          ) : (
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">Check-in</div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-60">Check-in</div>
            <div className="text-sm font-semibold truncate" data-testid="vm-checkin-event-name">{ev.name}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-60">Checked in</div>
            <div className="font-display text-2xl font-semibold leading-none" data-testid="vm-checkin-counter">
              {counts.checked_in}<span className="text-white/40 text-base font-normal"> / {counts.seated}</span>
            </div>
          </div>
        </div>
        <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full brand-gradient transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </header>

      {/* Body — scanner + actions */}
      <main className="flex-1 flex flex-col relative overflow-hidden">
        <div className="relative flex-1 bg-black">
          <video ref={videoRef} playsInline muted className={`absolute inset-0 w-full h-full object-cover ${scanning ? "" : "hidden"}`} data-testid="vm-checkin-video" />
          {!scanning && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
              <ScanLine size={54} className="text-white/40" strokeWidth={1.5} />
              <div className="text-white/70 text-sm max-w-xs">Tap <b>Start scanner</b> to point the camera at a visitor&apos;s QR code, or use manual entry.</div>
            </div>
          )}
          {scanning && (
            <>
              {/* Scanner viewfinder overlay */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="relative w-64 h-64 max-w-[70vw] max-h-[70vw]">
                  <div className="absolute inset-0 border-2 border-white/60 rounded-2xl" />
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-emerald-400 rounded-tl-2xl" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-emerald-400 rounded-tr-2xl" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-emerald-400 rounded-bl-2xl" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-emerald-400 rounded-br-2xl" />
                </div>
              </div>
              <button
                onClick={stopCamera}
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center"
                aria-label="Stop scanner"
                data-testid="vm-checkin-scan-stop"
              >
                <X size={18} />
              </button>
            </>
          )}
        </div>

        {/* Action bar */}
        <div
          className="px-4 py-4 grid grid-cols-2 gap-2 border-t border-white/10 bg-gray-950"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1rem)" }}
        >
          {!scanning ? (
            <button onClick={startCamera}
              className="col-span-2 h-14 rounded-xl brand-gradient text-white font-medium text-base flex items-center justify-center gap-2 active:brightness-95"
              data-testid="vm-checkin-scan-start">
              <Camera size={18} /> Start scanner
            </button>
          ) : (
            <button onClick={stopCamera}
              className="col-span-2 h-14 rounded-xl bg-white/10 text-white font-medium text-base flex items-center justify-center gap-2 border border-white/10 active:bg-white/20">
              <X size={18} /> Stop scanner
            </button>
          )}
          <button onClick={() => setManualOpen(true)}
            className="col-span-2 h-12 rounded-xl bg-white/10 text-white font-medium text-sm flex items-center justify-center gap-2 border border-white/10 active:bg-white/20"
            data-testid="vm-checkin-manual-open">
            <Keyboard size={16} /> Enter ticket number
          </button>
        </div>

        {/* Last result (compact) */}
        {(lastResult || preview) && !manualOpen && (
          <LastResult result={lastResult} />
        )}
      </main>

      {/* Preview / confirm card */}
      {preview && (
        <ConfirmCard
          preview={preview}
          onCancel={clearPreview}
          onConfirm={confirm}
          confirming={confirming}
        />
      )}

      {/* Manual entry sheet */}
      {manualOpen && (
        <Sheet onClose={() => setManualOpen(false)} title="Enter ticket number">
          <form onSubmit={submitManual} className="space-y-3">
            <input
              autoFocus
              inputMode="numeric"
              pattern="[0-9]*"
              value={manual}
              onChange={(e) => setManual(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 1023"
              className="w-full text-3xl font-mono tracking-wider text-center bg-gray-900 border border-white/10 rounded-xl py-4 outline-none focus:border-white/40"
              data-testid="vm-checkin-manual-input"
            />
            <button type="submit"
              className="w-full h-14 rounded-xl brand-gradient text-white font-medium text-base flex items-center justify-center gap-2 active:brightness-95"
              data-testid="vm-checkin-manual-submit">
              Look up
            </button>
          </form>
        </Sheet>
      )}
    </div>
  );
}

/* ============================================================
   Confirm card — big attendee preview with one-tap check-in
   ============================================================ */
function ConfirmCard({ preview, onCancel, onConfirm, confirming }) {
  const { ticket, attendee, already_checked_in } = preview;
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel} data-testid="vm-checkin-confirm-card">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white text-gray-900 rounded-t-3xl p-6 pb-8"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 2rem)" }}
      >
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-5" />

        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">Ticket</div>
        <div className="font-mono text-2xl font-semibold">#{ticket?.ticket_number}</div>

        {attendee ? (
          <>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">Attendee</div>
            <div className="font-display text-2xl font-semibold" data-testid="vm-checkin-confirm-name">{attendee.full_name}</div>
            {(attendee.organisation || attendee.position) && (
              <div className="text-sm text-gray-600 mt-0.5">{[attendee.position, attendee.organisation].filter(Boolean).join(" · ")}</div>
            )}
            {attendee.email && <div className="text-xs text-gray-500 mt-0.5">{attendee.email}</div>}
          </>
        ) : (
          <div className="mt-4 text-sm text-gray-500">Ticket registered — no attendee details on file.</div>
        )}

        {already_checked_in ? (
          <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm inline-flex items-center gap-2 w-full">
            <Clock size={16} /> This ticket has already been checked in
            {ticket?.checked_in_at && <span className="ml-1 font-mono">· {ticket.checked_in_at.replace("T", " ").slice(0, 19)}</span>}
          </div>
        ) : (
          <button
            onClick={onConfirm}
            disabled={confirming}
            className="mt-6 w-full h-16 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold text-lg flex items-center justify-center gap-2 active:brightness-95"
            data-testid="vm-checkin-confirm-btn"
          >
            <CheckCircle2 size={22} /> {confirming ? "Checking in…" : "Check In"}
          </button>
        )}
        <button onClick={onCancel}
          className="mt-3 w-full h-12 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm active:bg-gray-50"
          data-testid="vm-checkin-confirm-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Last-result banner (compact, shows after each scan/confirm)
   ============================================================ */
function LastResult({ result }) {
  if (!result) return null;
  if (result.error) {
    return (
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-red-500/10 border border-red-500/30 text-red-100 px-4 py-2.5 text-sm flex items-center gap-2" data-testid="vm-checkin-last-error">
          <XCircle size={16} /> {result.error}
        </div>
      </div>
    );
  }
  const name = result.attendee?.full_name;
  const num = result.ticket?.ticket_number;
  const dup = result.duplicate;
  return (
    <div className="px-4 pb-3">
      <div className={`rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 border ${dup ? "bg-amber-500/10 border-amber-500/30 text-amber-100" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"}`}
        data-testid="vm-checkin-last-result">
        {dup ? <Clock size={16} /> : <CheckCircle2 size={16} />}
        <span className="min-w-0 truncate">
          <b>#{num}</b>{name ? ` · ${name}` : ""}{dup ? " · already in" : " · checked in"}
        </span>
      </div>
    </div>
  );
}

/* ============================================================
   Shell + bottom sheet primitives
   ============================================================ */
function Shell({ children }) {
  return <div className="min-h-screen bg-gray-950 text-white flex flex-col" style={{ minHeight: "100dvh" }}>{children}</div>;
}
function Center({ children }) {
  return <div className="flex-1 flex items-center justify-center px-6"><div className="text-center">{children}</div></div>;
}
function Sheet({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-gray-900 text-white rounded-t-3xl p-6"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)" }}>
        <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mb-4" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">{title}</div>
        <div className="mt-3">{children}</div>
        <button onClick={onClose} className="mt-3 w-full h-12 rounded-xl border border-white/10 text-white/80 text-sm">Close</button>
      </div>
    </div>
  );
}
